import React, { useEffect, useState } from 'react';
import * as styles from './Settings.scss';
import intl from 'react-intl-universal';
import { Tab } from 'semantic-ui-react';
import Icon from 'components/Common/Icon';
import { TabProps } from 'semantic-ui-react/dist/commonjs/modules/Tab/Tab';
import _compact from 'lodash/compact';
import _get from 'lodash/get';
import _isNumber from 'lodash/isNumber';
import _memoize from 'lodash/memoize';
import _invert from 'lodash/invert';
import { RouteComponentProps } from 'react-router-dom';
import classnames from 'classnames';
import { ListViewLayoutHeader } from 'components/ListViewLayout/ListViewLayoutHeader';
import GeneralSettingsContainer from 'containers/Settings/GeneralSettingsContainer';
import AccountDetailsTabContainer from 'containers/Settings/AccountDetailsTabContainer';
import { IconSettingsContainer } from 'containers/Settings/IconSettingsContainer';
import Sso from 'components/Settings/SsoTab/Sso';
import Downloads from 'components/Settings/DownloadsTab/Downloads';
import { accountSelectors } from '@lifesize/nucleus';
import { ACCOUNT_FEATURE_DEVICE_SETTINGS_ENABLED, ACCOUNT_FEATURE_SSO } from 'constants/accountFeatureConstants';
import EcommerceThunks from 'thunks/ecommerceThunks';
import { useDispatch, useSelector } from 'react-redux';
import { selectEcommerceSupported } from 'selectors/ecommerceSelector';

const getTitle = _memoize(() => {
  return (
    <>
      <Icon classes={['icon-settings']} />
      <span>{intl.get('accountSettings')}</span>
    </>
  );
});

enum settingsRoutes {
  SETTINGS = '/settings',
  GROUP = '/settings/group',
  DEVICES = '/settings/icon',
  DETAILS = '/settings/details',
  DOWNLOADS = '/settings/downloads',
  SSO = '/settings/sso'
}

const tabs = [
  {
    'tab': '',
    index: 0
  },
  {
    'tab': 'group',
    index: 0
  },
  {
    'tab': 'icon',
    index: 1
  },
  {
    'tab': 'details',
    index: 2
  },
  {
    'tab': 'downloads',
    index: 3
  },
  {
    'tab': 'sso',
    index: 4
  }
];

const getInitialTab: (match: {}) => number = (match) => {
  return tabs.find((tabGroup) => tabGroup.tab = _get(match, 'params.tab', 'cloud'))?.index || 0;
};

const Settings = (props: RouteComponentProps<{}>) => {
  const { match, history } = props;
  const [selectedTab, setSelectedTab] = useState(getInitialTab(match));
  const hasDeviceSettings = useSelector(accountSelectors.getAccountFeature)(ACCOUNT_FEATURE_DEVICE_SETTINGS_ENABLED);
  const hasSsoAddOn = useSelector(accountSelectors.getAccountSettings)[ACCOUNT_FEATURE_SSO];
  const isEcommerceSupported = useSelector(selectEcommerceSupported);
  const showSsoTab = hasSsoAddOn || isEcommerceSupported;

  let tabIndex = 0;
  const supportedIndices = {
    [settingsRoutes.SETTINGS]: tabIndex,
    ...hasDeviceSettings && {[settingsRoutes.DEVICES]: tabIndex += 1},
    [settingsRoutes.DETAILS]: tabIndex += 1,
    [settingsRoutes.DOWNLOADS]: tabIndex += 1,
    ...showSsoTab && {[settingsRoutes.SSO]: tabIndex += 1},
  };

  const activeIndexToKey = _invert(supportedIndices);

  const dispatch = useDispatch();

  useEffect(
    () => {
      dispatch(EcommerceThunks.getEcommerceAccount());
    },
    []
  );

  const getCurrentIndex = () => {
    const tab = history.location.pathname || '/settings';
    return supportedIndices[tab];
  };

  const handleTabChange = (event: React.MouseEvent<HTMLDivElement>, tabData: TabProps) => {
    const activeIndex = tabData.activeIndex || 0;
    const url = activeIndexToKey[activeIndex];
    history.push(url);
    if (_isNumber(tabData.activeIndex) && selectedTab !== tabData.activeIndex) {
      setSelectedTab(tabData.activeIndex);
    }
  };

  const getTabPanes = () =>
    _compact([
      {
        menuItem: (
          {
            key: 'generalSettingsTab',
            content: (
              intl.get('cloudSettingsTabTitle')
            ),
            ['data-test']: 'tabItem.general'
          }),
        render: () => (
          <Tab.Pane key={settingsRoutes.SETTINGS} attached={false} className="contentBody">
            <GeneralSettingsContainer />
          </Tab.Pane>
        )
      },
      hasDeviceSettings && {
        menuItem: (
          {
            key: 'deviceSettingsTab',
            content: (
              intl.get('iconSettingsTabTitle')
            ),
            ['data-test']: 'tabItem.device'
          }
        ),
        render: () => (
          <Tab.Pane key={settingsRoutes.DEVICES} attached={false} className="contentBody">
            <IconSettingsContainer />
          </Tab.Pane>
        )
      },
      {
        menuItem: (
          {
            key: 'accountDetailsTab',
            content: (
              intl.get('accountDetailsTabTitle')
            ),
            ['data-test']: 'tabItem.details'
          }
        ),
        render: () => (
          <Tab.Pane key={settingsRoutes.DETAILS} attached={false} className="contentBody">
            <AccountDetailsTabContainer />
          </Tab.Pane>
        )
      },
      {
        menuItem: (
          {
            key: 'downloadsTab',
            content: (
              intl.get('downloadsTabTitle')
            ),
            ['data-test']: 'tabItem.downloads'
          }
        ),
        render: () => (
          <Tab.Pane key={settingsRoutes.DOWNLOADS} attached={false} className="contentBody">
            <Downloads />
          </Tab.Pane>
        )
      },
      (showSsoTab) && {
        menuItem: (
          {
            key: 'ssoTab',
            content: (
              intl.get('SsoTabTitle')
            ),
            ['data-test']: 'tabItem.sso'
          }
        ),
        render: () => (
          <Tab.Pane key={settingsRoutes.SSO} attached={false} className="contentBody">
            <Sso />
          </Tab.Pane>
        )
      }
    ]);

  return (
    <div className={styles.container}>
      <ListViewLayoutHeader title={getTitle()} />
      <div className={classnames(styles.tabsContainer, 'tabset')}>
        <Tab
          id={'tabContainer'}
          activeIndex={getCurrentIndex()}
          menu={{ secondary: true, pointing: true, ['data-test']: 'tabMenu' }}
          panes={getTabPanes()}
          onTabChange={handleTabChange}
        />
      </div>
    </div>
  );
};

export default Settings;
